import {
  Award,
  AwardData,
  AwardType,
  DisclaimerContent,
} from 'scenes/common/components/Awards';
import { FunnelType } from 'scenes/common/types';
import { HomeFunnelState } from 'scenes/home/types';
import { MotorFunnelState } from 'scenes/motor/types';
import { activeHomeAwards, activeMotorAwards } from 'shared/config';
import { homeAwardDefinitions, motorAwardDefinitions } from './data';

const awardDefinitions = (funnelType: string): AwardData => {
  switch (funnelType) {
    case FunnelType.Motor:
      return motorAwardDefinitions;
    case FunnelType.Home:
      return homeAwardDefinitions;
    // TODO implement award definitions for other products
    default:
      return {};
  }
};

const displayMotorAwards = (
  funnelState: Partial<MotorFunnelState>,
  award?: AwardType
): boolean => {
  const { step: currentStep, flow: currentFlow } = funnelState;
  const awards = awardDefinitions(FunnelType.Motor);

  // Don't display if any key data undefined
  if (!awards || !award || !currentStep || !currentFlow || !(award in awards)) {
    return false;
  }

  const { flowsToDisplay, stepsToIgnore } = awards[award];

  return (
    flowsToDisplay.includes(currentFlow) && !stepsToIgnore.includes(currentStep)
  );
};

export const motorAwardsToDisplay = (
  funnelState: Partial<MotorFunnelState>
) => {
  const activeAwards = activeMotorAwards();
  const awards: Array<Award> = [];
  activeAwards.forEach((award) => {
    if (displayMotorAwards(funnelState, award as AwardType)) {
      awards.push(awardDefinitions(FunnelType.Motor)[award]);
    }
  });
  return awards;
};

export const motorDisclaimersToDisplay = (
  funnelState: Partial<MotorFunnelState>
) => {
  const disclaimers: Array<DisclaimerContent> = [];
  motorAwardsToDisplay(funnelState).forEach((award) => {
    if (award.disclaimerSymbol && award.disclaimer) {
      disclaimers.push({
        disclaimerSymbol: award.disclaimerSymbol,
        disclaimer: award.disclaimer,
      });
    }
  });
  return disclaimers;
};

const displayHomeAwards = (
  funnelState: Partial<HomeFunnelState>,
  award?: AwardType
): boolean => {
  const { step: currentStep, flow: currentFlow } = funnelState;
  const awards = awardDefinitions(FunnelType.Home);

  // Don't display if any key data undefined
  if (!awards || !award || !currentStep || !currentFlow || !(award in awards)) {
    return false;
  }

  const { flowsToDisplay, stepsToIgnore } = awards[award];

  return (
    flowsToDisplay.includes(currentFlow) && !stepsToIgnore.includes(currentStep)
  );
};

export const homeAwardsToDisplay = (funnelState: Partial<HomeFunnelState>) => {
  const activeAwards = activeHomeAwards();
  const awards: Array<Award> = [];
  activeAwards.forEach((award) => {
    if (displayHomeAwards(funnelState, award as AwardType)) {
      awards.push(awardDefinitions(FunnelType.Home)[award]);
    }
  });
  return awards;
};

export const homeDisclaimersToDisplay = (
  funnelState: Partial<HomeFunnelState>
) => {
  const disclaimers: Array<DisclaimerContent> = [];
  homeAwardsToDisplay(funnelState).forEach((award) => {
    if (award.disclaimerSymbol && award.disclaimer) {
      disclaimers.push({
        disclaimerSymbol: award.disclaimerSymbol,
        disclaimer: award.disclaimer,
      });
    }
  });
  return disclaimers;
};

export const useAwards = (): {
  // TODO Implement for home, LL etc
  motorAwardsToDisplay: (
    funnelState: Partial<MotorFunnelState>
  ) => Array<Award>;
  motorDisclaimersToDisplay: (
    funnelState: Partial<MotorFunnelState>
  ) => Array<DisclaimerContent>;
  homeAwardsToDisplay: (funnelState: Partial<HomeFunnelState>) => Array<Award>;
  homeDisclaimersToDisplay: (
    funnelState: Partial<HomeFunnelState>
  ) => Array<DisclaimerContent>;
} => ({
  motorAwardsToDisplay,
  motorDisclaimersToDisplay,
  homeAwardsToDisplay,
  homeDisclaimersToDisplay,
});
